import { useContext } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import { formatDateTime } from 'services/utils';

// Attempts and payload of one delivery. Old deliveries (before the history was recorded) have no attempts.
export const WebhookDeliveryDetails = ({ delivery }) => {
  const i18n = useContext(i18nContext);
  const attempts = delivery.attempts || [];
  // deliveries in flight during the release have earlier attempts that were not recorded
  const missingAttempts = Math.max(0, (delivery.attemptCount || 0) - attempts.length);

  return (
    <div className={'webhook-delivery-details'}>
      {delivery.lastError && (
        <p className={'webhook-delivery-details-error'}>
          <span className={'webhook-delivery-details-label'}>
            {i18n.getMessage('webhookDeliveries.details.lastError')}
          </span>
          {delivery.lastError}
        </p>
      )}

      {delivery.processedAt && (
        <p>
          <span className={'webhook-delivery-details-label'}>
            {i18n.getMessage('webhookDeliveries.details.processedAt')}
          </span>
          {formatDateTime(delivery.processedAt)}
        </p>
      )}

      {delivery.nextAttemptAt && (
        <p>
          <span className={'webhook-delivery-details-label'}>
            {i18n.getMessage('webhookDeliveries.details.nextAttemptAt')}
          </span>
          {formatDateTime(delivery.nextAttemptAt)}
        </p>
      )}

      <p className={'webhook-delivery-details-title'}>{i18n.getMessage('webhookDeliveries.details.attempts')}</p>
      {attempts.length === 0 ? (
        <p className={'webhook-delivery-details-empty'}>
          {i18n.getMessage(
            delivery.attemptCount > 0
              ? 'webhookDeliveries.details.noAttemptHistory'
              : 'webhookDeliveries.details.noAttempts'
          )}
        </p>
      ) : (
        <>
          {missingAttempts > 0 && (
            <p className={'webhook-delivery-details-empty'}>
              {i18n.getMessage('webhookDeliveries.details.partialAttemptHistory', { count: missingAttempts })}
            </p>
          )}
          <table className={'webhook-delivery-attempts'}>
            <thead>
              <tr>
                <th>#</th>
                <th>{i18n.getMessage('webhookDeliveries.attempt.attemptedAt')}</th>
                <th>{i18n.getMessage('webhookDeliveries.attempt.result')}</th>
                <th>{i18n.getMessage('webhookDeliveries.attempt.duration')}</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((attempt, index) => (
                <tr key={`${attempt.attemptedAt}-${index}`} className={attempt.successful ? 'successful' : 'failed'}>
                  <td>{missingAttempts + index + 1}</td>
                  <td>{formatDateTime(attempt.attemptedAt)}</td>
                  <td>
                    {attempt.httpStatus !== null && attempt.httpStatus !== undefined
                      ? `HTTP ${attempt.httpStatus}`
                      : attempt.error || i18n.getMessage('webhookDeliveries.noResponse')}
                  </td>
                  <td>{i18n.getMessage('webhookDeliveries.attempt.durationMs', { duration: attempt.durationMs })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className={'webhook-delivery-details-title'}>{i18n.getMessage('webhookDeliveries.details.payload')}</p>
      <pre className={'webhook-delivery-payload'}>
        {typeof delivery.payload === 'string' ? delivery.payload : JSON.stringify(delivery.payload, null, 2)}
      </pre>
    </div>
  );
};

WebhookDeliveryDetails.propTypes = {
  delivery: PropTypes.object.isRequired
};
